// src/contexts/userContext.js
// Protected surfaces: review docs/protected-surfaces/session-pwa-transport.md and docs/protected-surfaces/avatar-onboarding.md before changing user bootstrap, auth gating, or onboarding-flag hydration behavior.

import React, { createContext, useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authApiClient from '../authApiClient';
import { safeGetItem, safeRemoveItem } from '../utils/storageSafe';
import { checkPWAAuth, initPWAAuthMonitoring, pwaAuthStorage } from '../utils/pwaAuthFallback';
import { bootstrapSession, isSessionReady, forceSessionReady, resetSession } from '../utils/authManager';
import axios from 'axios';
import { isAdminUser, resolveAppEntryPath } from '../utils/adminAccess';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

const AUTH_CONTEXT_DEBUG = String(process.env.REACT_APP_AUTH_FLOW_DEBUG || '').trim() === '1';
const authContextDebug = (...args) => {
  if (AUTH_CONTEXT_DEBUG) window.console.log(...args);
};

function resolveRequestedAppRedirect(search = '', fallback = '/app') {
  const requestedRedirect = new URLSearchParams(search || '').get('redirect');
  if (requestedRedirect && requestedRedirect.startsWith('/app')) {
    return requestedRedirect;
  }
  return fallback;
}

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [isPWAMode, setIsPWAMode] = useState(false);
  const isMountedRef = useRef(true);
  const location = useLocation(); // Use useLocation to get the current path
  const navigate = useNavigate(); // Add navigate hook
  const checkAuthInProgressRef = useRef(false); // Add a flag to prevent multiple calls
  const checkAuthResetTimerRef = useRef(null); // Separate timer for resetting the flag
  const lastPathCheckedRef = useRef(''); // Track the last path checked
  const lastAuthTimeRef = useRef(0); // Track the last time auth was checked
  const AUTH_THROTTLE_MS = 30000; // Minimum time between auth checks (30 seconds) - increased from 2s to reduce token refresh frequency
  const renderCount = useRef(0);

  // Add instanceId to help track provider instances
  const instanceId = useRef(`provider-${Math.random().toString(36).substr(2, 9)}`);

  authContextDebug(`🔄 [userContext ${instanceId.current}] RENDER #${++renderCount.current} at ${new Date().toISOString().substr(11, 12)} - bootstrapped: ${bootstrapped}, isLoading: ${isLoading}, user: ${user ? 'present' : 'null'}`);

  // Add state change trackers
  const prevBootstrappedRef = useRef(bootstrapped);
  const prevIsLoadingRef = useRef(isLoading);
  const prevUserRef = useRef(user);

  useEffect(() => {
    if (prevBootstrappedRef.current !== bootstrapped) {
      authContextDebug(`🔀 [userContext ${instanceId.current}] bootstrapped changed: ${prevBootstrappedRef.current} -> ${bootstrapped}`);
      prevBootstrappedRef.current = bootstrapped;
    }
    if (prevIsLoadingRef.current !== isLoading) {
      authContextDebug(`🔀 [userContext ${instanceId.current}] isLoading changed: ${prevIsLoadingRef.current} -> ${isLoading}`);
      prevIsLoadingRef.current = isLoading;
    }
    if (prevUserRef.current !== user) {
      authContextDebug(`🔀 [userContext ${instanceId.current}] user changed: ${prevUserRef.current ? 'present' : 'null'} -> ${user ? 'present' : 'null'}`);
      prevUserRef.current = user;
    }
  });

  // Add refresh attempt tracking to prevent loops
  const refreshAttemptTimeRef = useRef(0);
  const refreshFailedCountRef = useRef(0);
  const REFRESH_THROTTLE_MS = 30000; // 30 seconds between retry attempts after failures
  const MAX_REFRESH_ATTEMPTS = 2; // Maximum number of consecutive refresh attempts

  authContextDebug(`[userContext ${instanceId.current}] Provider initialized/rendered`);

  // PWA Mode Detection
  useEffect(() => {
    const detectPWAMode = () => {
      const isStandalone = window.navigator.standalone === true ||
        window.matchMedia('(display-mode: standalone)').matches;
      setIsPWAMode(isStandalone);
      authContextDebug(`[userContext ${instanceId.current}] PWA Mode detected: ${isStandalone}`);

      if (isStandalone) {
        authContextDebug(`[userContext ${instanceId.current}] Running in PWA mode - additional authentication checks may be needed`);
        // Initialize PWA auth monitoring
        initPWAAuthMonitoring();

        // ✅ REMOVED: Duplicate checkAuth call - mount effect handles this with proper timing
        authContextDebug(`[userContext ${instanceId.current}] PWA mode detected - auth check will run from mount effect`);
      }
    };

    detectPWAMode();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', detectPWAMode);

    return () => mediaQuery.removeEventListener('change', detectPWAMode);
  }, []);

  // Cleanup function
  useEffect(() => {
    authContextDebug(`[userContext ${instanceId.current}] Provider mounted`);
    isMountedRef.current = true;

    // Disabled proactive token refresh to prevent conflicts with axios interceptors
    // Let the server be the source of truth - axios interceptors will handle token refresh
    // Do NOT set bootstrapped here - wait for checkAuth() to complete first

    return () => {
      authContextDebug(`[userContext ${instanceId.current}] Provider unmounting - this could cause lost state!`);
      isMountedRef.current = false;                // ✅ only on real unmount
      if (checkAuthResetTimerRef.current) clearTimeout(checkAuthResetTimerRef.current);
    };
  }, []);

  // Helper to check if we should attempt a refresh
  const shouldAttemptRefresh = () => {
    const now = Date.now();

    // If we've had multiple failures, enforce a longer delay between attempts
    if (refreshFailedCountRef.current >= MAX_REFRESH_ATTEMPTS) {
      const timeSinceLastAttempt = now - refreshAttemptTimeRef.current;
      if (timeSinceLastAttempt < REFRESH_THROTTLE_MS) {
        authContextDebug(`[userContext ${instanceId.current}] Too many recent refresh failures (${refreshFailedCountRef.current}), waiting before retry`);
        return false;
      } else {
        // Reset counter after throttle period
        refreshFailedCountRef.current = 0;
      }
    }

    refreshAttemptTimeRef.current = now;
    return true;
  };

  const login = async (userData) => {
    try {
      authContextDebug(`[userContext ${instanceId.current}] login: Setting user data:`, userData);
      if (!isMountedRef.current) {
        authContextDebug(`[userContext ${instanceId.current}] Login failed - component unmounted!`);
        return;
      }

      // ✅ CRITICAL FIX: Clear the logged out flag on successful login
      if (typeof window !== 'undefined' && window.__LOGGED_OUT) {
        delete window.__LOGGED_OUT;
        authContextDebug(`[userContext ${instanceId.current}] Cleared window.__LOGGED_OUT flag`);
      }

      authContextDebug(`[userContext ${instanceId.current}] 🔍 Before setState - current user:`, user);
      authContextDebug(`[userContext ${instanceId.current}] 🔍 Before setState - isLoading:`, isLoading, 'bootstrapped:', bootstrapped);

      // Set all states synchronously
      setUser({
        ...userData,
        subscriptionActive: userData.subscriptionActive || false,
        subscriptionPlan: userData.subscriptionPlan || null,
        hasActivatedReferral: userData.hasActivatedReferral || false,
        referralCode: userData.referralCode || null
      });

      setIsLoading(false);
      setBootstrapped(true);
      forceSessionReady();

      authContextDebug(`[userContext ${instanceId.current}] User state should be updated after setState call`);

      // Update the last auth time when login is successful
      lastAuthTimeRef.current = Date.now();

      // ✅ NEW: Update PWA auth fallback for persistence
      const isRememberMe = safeGetItem('fusesoul_remember_me') === 'true' ||
        document.cookie.includes('FS_REMEMBER=1');
      if (isRememberMe) {
        authContextDebug(`[userContext ${instanceId.current}] Updating PWA auth fallback during login`);
        pwaAuthStorage.storeAuth(userData, true);
      }

      // Let's explicitly check if our state is updated
      setTimeout(() => {
        authContextDebug(`[userContext ${instanceId.current}] User state 100ms after login:`, user);
        authContextDebug(`[userContext ${instanceId.current}] 🔍 State check - user:`, user, 'isLoading:', isLoading, 'bootstrapped:', bootstrapped);
      }, 100);

      authContextDebug(`[userContext ${instanceId.current}] ✅ Login function completed successfully`);
    } catch (error) {
      console.error(`[userContext ${instanceId.current}] Login error:`, error);
      if (isMountedRef.current) {
        setIsLoading(false);
        setBootstrapped(true);
      }
    }
  };

  const logout = () => {
    authContextDebug(`[userContext ${instanceId.current}] logout called`);
    if (isMountedRef.current) {
      setUser(null);
      // Keep bootstrapped true even after logout
      setBootstrapped(true);
      // ✅ NEW: Reset session in auth manager
      resetSession();
      // Authentication state is now managed by HttpOnly cookies - no sessionStorage needed
      // Clear PWA auth fallback
      pwaAuthStorage.clearAuth();
      authContextDebug(`[userContext ${instanceId.current}] Cleared PWA auth fallback storage`);
      // ✅ Clear local remember flag too
      safeRemoveItem('fusesoul_remember_me');
    }
  };

  const checkAuth = async () => {
    const currentPath = location.pathname;
    const currentTime = Date.now();

    // ✅ CRITICAL FIX: Check logout flag first
    if (typeof window !== 'undefined' && window.__LOGGED_OUT) {
      authContextDebug('[userContext] checkAuth: User logged out, skipping auth check');
      if (isMountedRef.current) {
        setUser(null);
        setIsLoading(false);
        setBootstrapped(true);
      }
      return;
    }

    if (
      checkAuthInProgressRef.current ||
      (currentTime - lastAuthTimeRef.current < AUTH_THROTTLE_MS) ||
      !isMountedRef.current
    ) {
      authContextDebug('[userContext] checkAuth: Skipping check');
      return;
    }

    checkAuthInProgressRef.current = true;
    lastPathCheckedRef.current = currentPath;
    lastAuthTimeRef.current = currentTime;

    // Set bootstrapped immediately when we start checking - this ensures loading screen disappears
    if (isMountedRef.current) {
      setBootstrapped(true);
    }

    authContextDebug('[userContext] checkAuth: Start - Checking user authentication state');
    authContextDebug('[userContext] checkAuth: Current path is:', currentPath);
    authContextDebug('[userContext] checkAuth: PWA Mode:', isPWAMode, 'Standalone:', window.navigator.standalone);

    // Updated public paths - remove /app from public paths
    const publicPaths = ['/', '/about', '/contact', '/login', '/signup', '/pricing', '/payment-success', '/forgot-password', '/reset-password', '/unauthorized', '/reactivate', '/test-reactivate'];

    // Handle public paths with special logic for login page
    if (publicPaths.includes(currentPath)) {
      if (currentPath === '/login') {
        // ✅ CRITICAL FIX: Ensure XSRF cookie exists before any authenticated GETs
        try {
          await authApiClient.get('/health', { withCredentials: true });
          authContextDebug('[userContext] checkAuth: Seeded XSRF cookie via /health call');
        } catch (error) {
          authContextDebug('[userContext] checkAuth: /health failed (non-critical):', error?.message);
        }

        // ⛔ If server set FS_LOGOUT sentinel, clear it and skip probing
        if (document.cookie.includes('FS_LOGOUT=1')) {
          document.cookie = 'FS_LOGOUT=; Max-Age=0; Path=/';
          if (isMountedRef.current) setIsLoading(false);
          checkAuthInProgressRef.current = false;
          return;
        }

        // ⛔ /login: only probe if there's a session hint (Remember-Me or PWA fallback)
        const hasRemember = document.cookie.includes('FS_REMEMBER=1');
        const hasFallback = !!localStorage.getItem('pwa_auth_data');
        if (!hasRemember && !hasFallback) {
          authContextDebug('[userContext] /login with no remember/fallback — skipping session probe');
          if (isMountedRef.current) setIsLoading(false);
          checkAuthInProgressRef.current = false;
          return;
        }

        // ✅ NEW: Try bootstrapSession first (sequential: refresh → loadMe → sessionReady)
        authContextDebug('[userContext] checkAuth: On /login - trying bootstrapSession first...');
        try {
          const bootstrapResult = await bootstrapSession(authApiClient);
          if (bootstrapResult.success && bootstrapResult.user && isMountedRef.current) {
            authContextDebug('[userContext] checkAuth: ✅ Bootstrap successful, auto-logging in via remember me');
            setUser({
              ...bootstrapResult.user,
              _id: bootstrapResult.user._id ?? bootstrapResult.user.id,
              // Preserve avatar introduction flags if not in bootstrap result
              hasSeenEditAvatarIntroduction: bootstrapResult.user.hasSeenEditAvatarIntroduction !== undefined ? bootstrapResult.user.hasSeenEditAvatarIntroduction : (user?.hasSeenEditAvatarIntroduction !== undefined ? user.hasSeenEditAvatarIntroduction : false),
              hasSeenChatWithMeIntroduction: bootstrapResult.user.hasSeenChatWithMeIntroduction !== undefined ? bootstrapResult.user.hasSeenChatWithMeIntroduction : (user?.hasSeenChatWithMeIntroduction !== undefined ? user.hasSeenChatWithMeIntroduction : false),
              hasSeenJourneyGuideIntroduction: bootstrapResult.user.hasSeenJourneyGuideIntroduction !== undefined ? bootstrapResult.user.hasSeenJourneyGuideIntroduction : (user?.hasSeenJourneyGuideIntroduction !== undefined ? user.hasSeenJourneyGuideIntroduction : false)
            });
            setIsLoading(false);
            forceSessionReady(); // Mark session as ready
            // Force redirect from /login when user is authenticated
            if (location.pathname === '/login') {
              const nextPath = resolveRequestedAppRedirect(
                location.search,
                resolveAppEntryPath(bootstrapResult.user)
              );
              authContextDebug('[userContext] checkAuth: 🔄 Force redirect from /login', { nextPath });
              navigate(nextPath, { replace: true });
            }
            checkAuthInProgressRef.current = false;
            return;
          }
        } catch (bootstrapError) {
          authContextDebug('[userContext] checkAuth: Bootstrap failed, trying PWA fallback...', bootstrapError);
        }

        // Fallback to PWA auth if bootstrap didn't work
        authContextDebug('[userContext] checkAuth: On /login - checking Remember Me fallback...');
        const authResult = await checkPWAAuth(authApiClient);
        if (authResult.success && isMountedRef.current) {
          authContextDebug('[userContext] checkAuth: Found valid session on login page, auto-logging in');
          setUser({
            ...authResult.user,
            _id: authResult.user.id ?? authResult.user._id,
            // Preserve avatar introduction flags if not in auth result
            hasSeenEditAvatarIntroduction: authResult.user.hasSeenEditAvatarIntroduction !== undefined ? authResult.user.hasSeenEditAvatarIntroduction : (user?.hasSeenEditAvatarIntroduction !== undefined ? user.hasSeenEditAvatarIntroduction : false),
            hasSeenChatWithMeIntroduction: authResult.user.hasSeenChatWithMeIntroduction !== undefined ? authResult.user.hasSeenChatWithMeIntroduction : (user?.hasSeenChatWithMeIntroduction !== undefined ? user.hasSeenChatWithMeIntroduction : false),
            hasSeenJourneyGuideIntroduction: authResult.user.hasSeenJourneyGuideIntroduction !== undefined ? authResult.user.hasSeenJourneyGuideIntroduction : (user?.hasSeenJourneyGuideIntroduction !== undefined ? user.hasSeenJourneyGuideIntroduction : false)
          });
          setIsLoading(false);
          forceSessionReady(); // Mark session as ready
          // ✅ CRITICAL FIX: Force redirect from /login when user is authenticated
          // This fixes the PWA auto-login issue
          if (location.pathname === '/login') {
            const nextPath = resolveRequestedAppRedirect(
              location.search,
              resolveAppEntryPath(authResult.user)
            );
            authContextDebug('[userContext] checkAuth: 🔄 Force redirect from /login', { nextPath });
            navigate(nextPath, { replace: true });
          }
        } else {
          authContextDebug('[userContext] checkAuth: No valid session on login page');
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        }
      } else {
        // Other public pages: no auth check needed
        authContextDebug(`[userContext] checkAuth: Skipping authentication check for public path: ${currentPath}`);
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
      checkAuthInProgressRef.current = false;
      return;
    }

    // Special handling for reactivate page - we need auth but don't want to redirect away from it
    const isReactivatePage = currentPath === '/reactivate';

    try {
      authContextDebug('[userContext] checkAuth: 🚀 Starting authentication check...');

      // ✅ NEW: Try bootstrapSession first (sequential: refresh → loadMe → sessionReady)
      authContextDebug('[userContext] checkAuth: Trying bootstrapSession first...');
      let authResult = null;
      try {
        const bootstrapResult = await bootstrapSession(authApiClient);
        if (bootstrapResult.success && bootstrapResult.user) {
          authContextDebug('[userContext] checkAuth: ✅ Bootstrap successful via:', bootstrapResult.user.username);
          // Use bootstrap result as auth result
          authResult = {
            success: true,
            user: bootstrapResult.user,
            method: 'bootstrap'
          };
        }
      } catch (bootstrapError) {
        authContextDebug('[userContext] checkAuth: Bootstrap failed, trying PWA fallback...', bootstrapError);
      }

      // Fallback to PWA auth if bootstrap didn't work
      if (!authResult || !authResult.success) {
        authContextDebug('[userContext] checkAuth: Bootstrap failed or no user, trying PWA auth...');
        authResult = await checkPWAAuth(authApiClient);
        authContextDebug('[userContext] checkAuth: 🔍 PWA auth result:', authResult);
      }

      if (!isMountedRef.current) return;

      if (authResult.success) {
        authContextDebug('[userContext] checkAuth: ✅ Authentication SUCCESS via:', authResult.method);

        // 🔍 DEBUG: Log what we got back
        authContextDebug('[userContext] checkAuth: 🔍 User data from auth:', {
          userId: authResult.user?.id || authResult.user?._id,
          username: authResult.user?.username,
          method: authResult.method,
          hasFallbackExpiry: !!authResult.expiresAt
        });

        // 🔍 DEBUG: Check localStorage fallback state
        const fallbackData = localStorage.getItem('pwa_auth_data');
        const fallbackExpires = localStorage.getItem('pwa_auth_expires');
        authContextDebug('[userContext] checkAuth: 🔍 LocalStorage fallback state:', {
          hasData: !!fallbackData,
          hasExpires: !!fallbackExpires,
          expiresValue: fallbackExpires ? new Date(parseInt(fallbackExpires)).toISOString() : null
        });

        // 🔍 DEBUG: Check current cookies
        authContextDebug('[userContext] checkAuth: 🔍 Current cookies:', {
          hasAccessToken: document.cookie.includes('accessToken='),
          hasRefreshToken: document.cookie.includes('refreshToken='),
          hasFSRemember: document.cookie.includes('FS_REMEMBER='),
          allCookies: document.cookie || 'No cookies present'
        });

      } else {
        authContextDebug('[userContext] checkAuth: ❌ PWA auth failed, trying token refresh...');

        // 🔍 DEBUG: Log why PWA auth failed
        authContextDebug('[userContext] checkAuth: 🔍 PWA auth failure details:', {
          error: authResult.error,
          method: authResult.method
        });

        // ✅ FIX: Don't block on PWA guard failure when not in PWA mode
        const isPWAMode = window.navigator.standalone === true ||
          window.matchMedia('(display-mode: standalone)').matches;

        if (!isPWAMode && authResult.error === 'no_session_hint') {
          authContextDebug('[userContext] checkAuth: ℹ️ Not in PWA mode, ignoring no_session_hint, trying direct /users/me...');

          // Try direct /users/me call as fallback
          try {
            const directResponse = await authApiClient.get('/users/me');
            authContextDebug('[userContext] checkAuth: ✅ Direct /users/me SUCCESS');

            // Normalize the response data
            const raw = directResponse.data;
            const normalized = raw ? { ...raw, _id: raw._id ?? raw.id } : null;

            if (normalized) {
              authResult = { success: true, user: normalized, method: 'direct' };
            } else {
              authContextDebug('[userContext] checkAuth: ❌ Direct /users/me returned no user data');
              if (isMountedRef.current) {
                setUser(null);
                setIsLoading(false);
              }
              checkAuthInProgressRef.current = false;
              return;
            }
          } catch (directError) {
            authContextDebug('[userContext] checkAuth: ❌ Direct /users/me also failed:', directError.message);
            // Continue to token refresh fallback
          }
        }

        // If we still don't have success, try token refresh as last resort
        if (!authResult.success) {
          try {
            authContextDebug('[userContext] checkAuth: 🔄 Attempting token refresh...');
            await tokenExpiryCheck();

            // If token refresh succeeded, try PWA auth again
            authContextDebug('[userContext] checkAuth: 🔄 Token refresh succeeded, retrying PWA auth...');
            const retryAuthResult = await checkPWAAuth(authApiClient);

            if (retryAuthResult.success) {
              authContextDebug('[userContext] checkAuth: ✅ Authentication SUCCESS after token refresh via:', retryAuthResult.method);
              // Use the retry result instead
              authResult = retryAuthResult;
            } else {
              authContextDebug('[userContext] checkAuth: ❌ Authentication still failed after token refresh');
              // Both methods failed, clear user
              if (isMountedRef.current) {
                setUser(null);
                setIsLoading(false);
              }
              checkAuthInProgressRef.current = false;
              return;
            }
          } catch (refreshError) {
            authContextDebug('[userContext] checkAuth: ❌ Token refresh also failed:', refreshError.message);
            // Both methods failed, clear user
            if (isMountedRef.current) {
              setUser(null);
              setIsLoading(false);
            }
            checkAuthInProgressRef.current = false;
            return;
          }
        }
      }

      // ✅ FIX: Normalize user data to ensure both _id and id are present
      const raw = authResult.user;
      const normalized = raw ? { ...raw, _id: raw._id ?? raw.id } : null;

      if (!normalized) {
        authContextDebug('[userContext] checkAuth: ❌ No user data to process');
        if (isMountedRef.current) {
          setUser(null);
          setIsLoading(false);
        }
        checkAuthInProgressRef.current = false;
        return;
      }

      const userData = {
        ...normalized,
        subscriptionActive: normalized.subscriptionActive || false,
        subscriptionPlan: normalized.subscriptionPlan || null,
        hasEverSubscribed: normalized.hasEverSubscribed || false,
        // Use API values directly - normalized should have these from /users/me
        // Only fallback to existing user values if API doesn't provide them
        hasSeenEditAvatarIntroduction: normalized.hasSeenEditAvatarIntroduction !== undefined ? normalized.hasSeenEditAvatarIntroduction : (user?.hasSeenEditAvatarIntroduction !== undefined ? user.hasSeenEditAvatarIntroduction : undefined),
        hasSeenChatWithMeIntroduction: normalized.hasSeenChatWithMeIntroduction !== undefined ? normalized.hasSeenChatWithMeIntroduction : (user?.hasSeenChatWithMeIntroduction !== undefined ? user.hasSeenChatWithMeIntroduction : undefined),
        hasSeenJourneyGuideIntroduction: normalized.hasSeenJourneyGuideIntroduction !== undefined ? normalized.hasSeenJourneyGuideIntroduction : (user?.hasSeenJourneyGuideIntroduction !== undefined ? user.hasSeenJourneyGuideIntroduction : undefined),
        hasActivatedReferral: normalized.hasActivatedReferral || false,
        referralCode: normalized.referralCode || null
      };

      authContextDebug('[userContext] checkAuth: Processed user data:', {
        _id: userData._id,
        id: userData.id,
        username: userData.username,
        role: userData.role,
        is_staff: userData.is_staff,
        subscriptionActive: userData.subscriptionActive,
        subscriptionPlan: userData.subscriptionPlan,
        hasEverSubscribed: userData.hasEverSubscribed,
        hasSeenEditAvatarIntroduction: userData.hasSeenEditAvatarIntroduction,
        hasSeenChatWithMeIntroduction: userData.hasSeenChatWithMeIntroduction,
        hasSeenJourneyGuideIntroduction: userData.hasSeenJourneyGuideIntroduction,
        authMethod: authResult.method
      });

      if (isMountedRef.current) {
        setUser(userData);
        setIsLoading(false);
        // ✅ NEW: Mark session as ready after successful auth
        forceSessionReady();

        // ✅ NEW: Sync PWA fallback if authenticated via cookies/bootstrap
        const isRememberMe = document.cookie.includes('FS_REMEMBER=1') ||
          safeGetItem('fusesoul_remember_me') === 'true';

        if (isRememberMe && (authResult.method === 'cookies' || authResult.method === 'bootstrap' || authResult.method === 'direct')) {
          authContextDebug(`[userContext] 🔄 Syncing PWA fallback for authenticated user (method: ${authResult.method})`);
          pwaAuthStorage.storeAuth(userData, true);
        }
      }

      // Only handle redirects for /app routes, not for /reactivate
      if (currentPath.startsWith('/app')) {
        authContextDebug('[userContext] checkAuth: Handling redirects for /app route');

        if (isAdminUser(userData)) {
          if (!currentPath.startsWith('/app/admin')) {
            authContextDebug('[userContext] checkAuth: Admin user on non-admin route, redirecting to /app/admin');
            navigate('/app/admin', { replace: true });
          }
          return userData;
        }

        // Check trial status first
        if (userData.isOnTrial) {
          authContextDebug('[userContext] checkAuth: User is on trial');

          if (userData.trialExpired) {
            authContextDebug('[userContext] checkAuth: Trial has expired, redirecting to trial expired page');
            navigate('/trial-expired', { replace: true });
            return userData;
          } else {
            authContextDebug('[userContext] checkAuth: Trial is active, allowing access');
            return userData;
          }
        }

        // Check subscription status and redirect accordingly
        if (!userData.subscriptionActive) {
          authContextDebug('[userContext] checkAuth: User subscription not active');

          if (userData.hasEverSubscribed) {
            authContextDebug('[userContext] checkAuth: User has previously subscribed, hasEverSubscribed=true');
            authContextDebug('[userContext] checkAuth: Redirecting to reactivate page from:', currentPath);
            navigate('/reactivate', { replace: true, state: { redirectReason: 'reactivation_required' } });
            return userData;
          } else {
            authContextDebug('[userContext] checkAuth: User never subscribed, hasEverSubscribed=false');
            authContextDebug('[userContext] checkAuth: Redirecting to pricing page from:', currentPath);
            navigate('/pricing', { replace: true, state: { redirectReason: 'subscription_required' } });
            return userData;
          }
        }
      }
      // Special handling for the trial expired page - don't redirect away from it
      else if (currentPath === '/trial-expired') {
        authContextDebug('[userContext] checkAuth: On trial expired page, allowing access');
        return userData;
      }
      // Special handling for the reactivate page
      else if (isReactivatePage) {
        authContextDebug('[userContext] checkAuth: On reactivate page, checking if user should be here');

        // If user has active subscription, they shouldn't be on reactivate page
        if (userData.subscriptionActive) {
          const nextPath = resolveAppEntryPath(userData);
          authContextDebug('[userContext] checkAuth: User has active subscription, redirecting from reactivate', { nextPath });
          navigate(nextPath, { replace: true });
          return userData;
        }

        // If user never had a subscription, they shouldn't be on reactivate page
        if (!userData.hasEverSubscribed) {
          authContextDebug('[userContext] checkAuth: User never subscribed, redirecting from reactivate to pricing');
          navigate('/pricing', { replace: true, state: { redirectReason: 'subscription_required' } });
          return userData;
        }

        // Otherwise they should stay on reactivate page (inactive + hasEverSubscribed)
        authContextDebug('[userContext] checkAuth: User correctly on reactivate page (inactive + hasEverSubscribed)');
      }

      return userData;
    } catch (error) {
      console.error('[userContext] Auth check error:', error);
      if (isMountedRef.current) {
        setUser(null);
        setIsLoading(false);
      }
      return null;
    } finally {
      if (isMountedRef.current) {
        if (checkAuthResetTimerRef.current) clearTimeout(checkAuthResetTimerRef.current);
        checkAuthResetTimerRef.current = setTimeout(() => {
          checkAuthInProgressRef.current = false;
        }, 1000);
      }
    }
  };

  // Add error type helpers
  const isAuthError = err =>
    axios.isAxiosError(err) &&
    err.response &&
    (err.response.status === 401 || err.response.status === 403);

  const isNetworkError = err =>
    axios.isAxiosError(err) && !err.response; // no response object = cannot reach server

  const tokenExpiryCheck = async () => {
    authContextDebug(`[userContext ${instanceId.current}] tokenExpiryCheck: Check if access token is expired`);
    try {
      await authApiClient.get('/users/me');
      authContextDebug(`[userContext ${instanceId.current}] tokenExpiryCheck: Access token is valid`);
      return;
    } catch (error) {
      if (isAuthError(error)) {
        // If it's a 401/403, let the Axios interceptor handle the refresh
        // This avoids double refresh attempts and race conditions
        authContextDebug(`[userContext ${instanceId.current}] tokenExpiryCheck: Access token expired (401/403), letting Axios interceptor handle refresh`);
        throw error; // Let the interceptor catch this and handle refresh
      } else if (isNetworkError(error)) {
        console.warn('[userContext] tokenExpiryCheck: Network/server error. Keeping session, will retry later.');
        // Do NOT clear session flag
        throw error;
      } else {
        console.error('[userContext] tokenExpiryCheck: Unexpected error:', error);
        throw error;
      }
    }
  };

  // Removed proactiveTokenRefresh to avoid double refresh attempts
  // The Axios interceptor now handles all token refresh automatically

  // 🔧 CRITICAL FIX: Run checkAuth immediately on mount for PWA cold-open
  useEffect(() => {
    authContextDebug('[userContext] useEffect: Component mounted - running initial auth check for PWA cold-open');

    // Add a small delay to prevent race conditions with login
    const delayedCheckAuth = () => {
      authContextDebug('[userContext] 🔍 Delayed checkAuth starting...');
      checkAuth();
    };

    // Wait longer before running checkAuth to avoid conflicts with login
    // PWA needs more time for cookies to propagate
    const delay = isPWAMode ? 800 : 300;
    authContextDebug(`[userContext] 🔍 Using ${delay}ms delay for checkAuth (PWA: ${isPWAMode})`);
    setTimeout(delayedCheckAuth, delay);

    return () => {
      if (checkAuthResetTimerRef.current) clearTimeout(checkAuthResetTimerRef.current);
    };
  }, []);                                         // ✅ mount-only

  // 🔧 ADDITIONAL: Handle path changes separately to avoid duplicate auth checks
  useEffect(() => {
    authContextDebug('[userContext] useEffect: Location changed, checking if auth check needed');

    // Only check auth if the path has changed and we haven't checked recently
    const currentTime = Date.now();
    if (
      lastPathCheckedRef.current !== location.pathname &&
      (currentTime - lastAuthTimeRef.current >= AUTH_THROTTLE_MS)
    ) {
      authContextDebug('[userContext] useEffect: Path changed, running checkAuth');
      checkAuth();
    } else {
      authContextDebug('[userContext] useEffect: Skipping auth check - path already checked or checked recently');
    }
  }, [location.pathname]); // Depend on location.pathname

  authContextDebug('[userContext] Current state - user:', user ? {
    id: user.id,
    username: user.username,
    subscriptionActive: user.subscriptionActive,
    subscriptionPlan: user.subscriptionPlan,
    hasEverSubscribed: user.hasEverSubscribed
  } : null, 'isLoading:', isLoading);

  return (
    <UserContext.Provider value={{ user, setUser, login, logout, checkAuth, isLoading, bootstrapped, isPWAMode }}>
      {children}
    </UserContext.Provider>
  );
};

